import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import { Users, Heart, Globe, CalendarBlank, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const FIELDS = [
  { name: "name", label: "Name", type: "text" },
  { name: "batch", label: "Batch year", type: "text" },
  { name: "company", label: "Company", type: "text" },
  { name: "role", label: "Role", type: "text" },
  { name: "chapter", label: "Chapter", type: "text", hint: "City — Bengaluru / Mumbai / London…" },
  { name: "mentoring", label: "Available as mentor", type: "switch" },
];

export default function Alumni() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/alumni");
    setList(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (a) => setModal({ open: true, editing: a });
  const close = () => setModal({ open: false, editing: null });

  const save = async (values) => {
    try {
      if (modal.editing) {
        await api.patch(`/alumni/${modal.editing.alumni_id}`, values);
        toast.success("Alumni updated");
      } else {
        await api.post("/alumni", values);
        toast.success("Alumni added");
      }
      close(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.name} from alumni directory?`)) return;
    try { await api.delete(`/alumni/${a.alumni_id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <PageHeader
        overline="M13 · Outcomes"
        title={t("modules.alumni.title")}
        description={t("modules.alumni.subtitle")}
        action={
          <Button onClick={openCreate} className="rounded-md bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_42%)]" data-testid="new-alumni-btn">
            <Plus size={14} className="mr-1.5"/> Add alumni
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          {[
            { I: Users, l: "Alumni registered", v: list.length, t: "across batches" },
            { I: Globe, l: "Chapters", v: new Set(list.map(a => a.chapter)).size, t: "cities" },
            { I: Heart, l: "Mentors active", v: list.filter(a => a.mentoring).length, t: "available" },
            { I: CalendarBlank, l: "Events YTD", v: 24, t: "next: Mar 12" },
          ].map(k => (
            <div key={k.l} className="bg-white p-5">
              <k.I size={20} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
              <div className="eos-overline mt-3">{k.l}</div>
              <div className="font-heading font-extrabold text-3xl mt-1 tracking-tight">{k.v}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{k.t}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(a => (
            <div key={a.alumni_id} className="eos-card eos-card-hover" data-testid={`alumni-${a.alumni_id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-[hsl(244_53%_20%)] text-white flex items-center justify-center font-heading font-bold">
                  {a.name.split(" ").map(s => s[0]).join("").slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-semibold text-base tracking-tight truncate">{a.name}</div>
                  <div className="text-xs text-muted-foreground">Batch of {a.batch} · {a.chapter}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" onClick={() => openEdit(a)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${a.alumni_id}`}><PencilSimple size={13}/></Button>
                  <Button variant="ghost" onClick={() => remove(a)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${a.alumni_id}`}><Trash size={13}/></Button>
                </div>
              </div>
              <div className="mt-4 text-sm">{a.role} · <span className="text-muted-foreground">{a.company}</span></div>
              <div className="mt-4 flex items-center justify-between">
                <span className={`text-[11px] px-2 py-1 rounded-md ${a.mentoring ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>
                  {a.mentoring ? "Mentor available" : "Not mentoring"}
                </span>
                <Button variant="ghost" className="h-7 px-2 text-xs rounded">Connect</Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit alumni" : "Add alumni"}
        fields={FIELDS}
        initial={modal.editing || { mentoring: false, batch: "2020" }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Add alumni"}
      />
    </div>
  );
}
